import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Type, Save, Upload, X, GripVertical, RotateCcw } from 'lucide-react';
import axios from 'axios';
import LoginLogoEditor from './LoginLogoEditor';

// Factory-default navbar pages per role. Source of truth for the initial
// state, the deactivate/reactivate UI, and the "Reset Order to Defaults"
// button. Keep in sync with Navbar.js getNavItemConfig().
const DEFAULT_NAV_ORDER = {
  admin: ['home', 'clients', 'appointments', 'availability', 'timeOff', 'recommendations', 'profile', 'users', 'messaging', 'quickBuy', 'inventory', 'aboutUs', 'contactUs', 'admin'],
  employee: ['home', 'clients', 'appointments', 'availability', 'timeOff', 'recommendations', 'profile', 'quickBuy', 'aboutUs', 'contactUs'],
  client: ['home', 'appointments', 'availability', 'recommendations', 'profile', 'aboutUs', 'contactUs'],
};

// Display labels for nav keys not covered by branding.navLabels (used by the
// Deactivated Pages reactivate list). Falls through to a humanized key.
const NAV_KEY_FALLBACK_LABELS = {
  aboutUs: 'About Us',
  contactUs: 'Contact Us',
  timeOff: 'Time Off',
  quickBuy: 'Quick Buy',
};

const API_URL = process.env.REACT_APP_API_URL || '/api';

const SiteBrandingEditor = () => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [activeNavTab, setActiveNavTab] = useState('admin'); // 'admin', 'employee', 'client'
  const [branding, setBranding] = useState({
    siteName: 'Dog Grooming',
    siteIcon: '🐕',
    logoType: 'emoji', // 'emoji' or 'image'
    logoImage: null, // base64 image data
    logoTextColor: '#1f2937', // Color for logo text
    logoBackgroundColor: '#2563eb', // Background color for logo
    logoFontFamily: 'system-ui', // Font family for logo text
    navbarBackgroundColor: '#ffffff', // Background color for navbar
    navStyle: 'auto', // 'auto' | 'horizontal' | 'hamburger'
    loginTitle: 'Dog Grooming System',
    loginSubtitle: 'Sign in to your account',
    loginTextColor: '#1f2937', // Color for login title
    loginFontFamily: 'system-ui', // Font family for login title
    // Individual logos for each position
    loginLogoLeft: {
      enabled: false,
      type: 'emoji', // 'emoji' or 'image'
      icon: '🐕',
      image: null,
      backgroundColor: '#2563eb',
      size: 48, // Size in pixels
    },
    loginLogoAbove: {
      enabled: true,
      type: 'emoji',
      icon: '🐕',
      image: null,
      backgroundColor: '#2563eb',
      size: 64, // Size in pixels (above logo is larger by default)
    },
    loginLogoRight: {
      enabled: false,
      type: 'emoji',
      icon: '🐕',
      image: null,
      backgroundColor: '#2563eb',
      size: 48, // Size in pixels
    },
    navLabels: {
      home: 'Home',
      clients: 'Clients',
      appointments: 'Appointments',
      availability: 'Availability',
      timeOff: 'Time Off',
      recommendations: 'Recommendations',
      profile: 'Profile',
      users: 'Users',
      messaging: 'Messaging',
      quickBuy: 'Quick Buy',
      inventory: 'Inventory',
      aboutUs: 'About Us',
      contactUs: 'Contact Us',
      admin: 'Admin',
    },
    navLabelStyles: {
      home: { color: '#374151', fontFamily: 'system-ui' },
      clients: { color: '#374151', fontFamily: 'system-ui' },
      appointments: { color: '#374151', fontFamily: 'system-ui' },
      availability: { color: '#374151', fontFamily: 'system-ui' },
      timeOff: { color: '#374151', fontFamily: 'system-ui' },
      recommendations: { color: '#374151', fontFamily: 'system-ui' },
      profile: { color: '#374151', fontFamily: 'system-ui' },
      users: { color: '#374151', fontFamily: 'system-ui' },
      messaging: { color: '#374151', fontFamily: 'system-ui' },
      quickBuy: { color: '#374151', fontFamily: 'system-ui' },
      inventory: { color: '#374151', fontFamily: 'system-ui' },
      aboutUs: { color: '#374151', fontFamily: 'system-ui' },
      contactUs: { color: '#374151', fontFamily: 'system-ui' },
      admin: { color: '#374151', fontFamily: 'system-ui' },
    },
    // Separate nav configurations for each role
    navOrder: {
      admin: [...DEFAULT_NAV_ORDER.admin],
      employee: [...DEFAULT_NAV_ORDER.employee],
      client: [...DEFAULT_NAV_ORDER.client],
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/business-info/site_branding`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.info_value) {
        const savedBranding = JSON.parse(response.data.info_value);
        // Merge saved branding with defaults to ensure new fields are included
        setBranding(prev => ({
          ...prev,
          ...savedBranding,
          navLabels: {
            ...prev.navLabels,
            ...(savedBranding.navLabels || {}),
          },
          navLabelStyles: {
            ...prev.navLabelStyles,
            ...(savedBranding.navLabelStyles || {}),
          },
          navOrder: savedBranding.navOrder 
            ? (Array.isArray(savedBranding.navOrder) 
                ? { admin: savedBranding.navOrder, employee: savedBranding.navOrder, client: savedBranding.navOrder } 
                : savedBranding.navOrder)
            : prev.navOrder,
          loginLogoLeft: {
            ...prev.loginLogoLeft,
            ...(savedBranding.loginLogoLeft || {}),
          },
          loginLogoAbove: {
            ...prev.loginLogoAbove,
            ...(savedBranding.loginLogoAbove || {}),
          },
          loginLogoRight: {
            ...prev.loginLogoRight,
            ...(savedBranding.loginLogoRight || {}),
          },
        }));
      }
    } catch (error) {
      console.error('Load branding error:', error);
      // Keep defaults if not found
    } finally {
      setLoading(false);
    }
  };

  const handleSiteName = (value) => {
    setBranding(prev => ({ ...prev, siteName: value }));
  };

  const handleSiteIcon = (value) => {
    setBranding(prev => ({ ...prev, siteIcon: value }));
  };

  const handleLogoTypeChange = (type) => {
    setBranding(prev => ({ ...prev, logoType: type }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setBranding(prev => ({ 
        ...prev, 
        logoImage: reader.result,
        logoType: 'image'
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setBranding(prev => ({ 
      ...prev, 
      logoImage: null,
      logoType: 'emoji'
    }));
  };

  const handleLoginTitle = (value) => {
    setBranding(prev => ({ ...prev, loginTitle: value }));
  };

  const handleLoginLogoToggle = (position) => {
    setBranding(prev => ({
      ...prev,
      [position]: {
        ...prev[position],
        enabled: !prev[position].enabled
      }
    }));
  };

  const handleLoginLogoTypeChange = (position, type) => {
    setBranding(prev => ({
      ...prev,
      [position]: {
        ...prev[position],
        type: type
      }
    }));
  };

  const handleLoginLogoIcon = (position, value) => {
    setBranding(prev => ({
      ...prev,
      [position]: {
        ...prev[position],
        icon: value
      }
    }));
  };

  const handleLoginLogoImageUpload = async (position, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);
      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setBranding(prev => ({
        ...prev,
        [position]: {
          ...prev[position],
          image: response.data.url,
          type: 'image'
        }
      }));
    } catch (error) {
      toast.error('Failed to upload image: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRemoveLoginLogoImage = (position) => {
    setBranding(prev => ({
      ...prev,
      [position]: {
        ...prev[position],
        image: null,
        type: 'emoji'
      }
    }));
  };

  const handleLoginLogoBackgroundColor = (position, color) => {
    setBranding(prev => ({
      ...prev,
      [position]: {
        ...prev[position],
        backgroundColor: color
      }
    }));
  };

  const handleLoginLogoSize = (position, size) => {
    setBranding(prev => ({
      ...prev,
      [position]: {
        ...prev[position],
        size: size
      }
    }));
  };

  const handleNavLabelChange = (key, value) => {
    setBranding(prev => ({
      ...prev,
      navLabels: {
        ...prev.navLabels,
        [key]: value,
      },
    }));
  };

  // Drag and drop handlers for reordering nav items
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    setDraggedIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const currentOrder = [...branding.navOrder[activeNavTab]];
    const [removed] = currentOrder.splice(draggedIndex, 1);
    currentOrder.splice(index, 0, removed);

    setBranding(prev => ({
      ...prev,
      navOrder: {
        ...prev.navOrder,
        [activeNavTab]: currentOrder
      }
    }));
    setDraggedIndex(index);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDraggedIndex(null);
  };

  // Get items available for each role
  const getAvailableItemsForRole = (role) => {
    return DEFAULT_NAV_ORDER[role] || DEFAULT_NAV_ORDER.client;
  };

  // Deactivate a page: remove it from the role's navOrder so it stops
  // rendering in the live navbar. The page key remains a known default and
  // appears in the "Deactivated Pages" section below where it can be
  // reactivated. Custom label / color / font are kept intact.
  const handleDeactivatePage = (key) => {
    const role = activeNavTab;
    const current = branding.navOrder[role] || [];
    if (!current.includes(key)) return;
    setBranding(prev => ({
      ...prev,
      navOrder: {
        ...prev.navOrder,
        [role]: current.filter(k => k !== key),
      },
    }));
    toast.success(`Deactivated "${branding.navLabels[key] || NAV_KEY_FALLBACK_LABELS[key] || key}". Click Save Branding to keep the change.`);
  };

  // Reactivate a previously-deactivated page by appending it to the end of
  // the role's navOrder. The user can then drag it to their preferred
  // position before saving.
  const handleReactivatePage = (key) => {
    const role = activeNavTab;
    const current = branding.navOrder[role] || [];
    if (current.includes(key)) return;
    setBranding(prev => ({
      ...prev,
      navOrder: {
        ...prev.navOrder,
        [role]: [...current, key],
      },
    }));
    toast.success(`Reactivated "${branding.navLabels[key] || NAV_KEY_FALLBACK_LABELS[key] || key}" at the end. Drag it where you want, then click Save Branding.`);
  };

  // Reset the active role's nav order completely back to factory defaults.
  // Discards any reordering the user did. Confirmation required.
  const handleResetNavOrderToDefault = () => {
    const role = activeNavTab;
    const defaults = DEFAULT_NAV_ORDER[role] || [];
    if (!window.confirm(
      `Reset the ${role} navbar to its factory default order and pages?\n\n` +
      'This re-adds any removed pages and resets the order back to defaults. ' +
      'Your custom labels, colors, and fonts are kept. Other roles are not affected. ' +
      'You still need to click Save Branding to persist the change.'
    )) {
      return;
    }
    setBranding(prev => ({
      ...prev,
      navOrder: {
        ...prev.navOrder,
        [role]: [...defaults],
      },
    }));
    toast.success(`${role.charAt(0).toUpperCase() + role.slice(1)} navbar reset to factory defaults. Click Save Branding to keep the change.`);
  };

  // Filter navOrder to only show items available for the current role
  const getFilteredNavOrder = () => {
    const availableItems = getAvailableItemsForRole(activeNavTab);
    return branding.navOrder[activeNavTab].filter(item => availableItems.includes(item));
  };


  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      
      // Create a clean copy of branding data, removing any undefined or problematic values
      const cleanBranding = JSON.parse(JSON.stringify(branding));
      
      // Stringify the branding data
      const brandingString = JSON.stringify(cleanBranding);
      
      // Debug: Log what we're sending
      console.log('Branding object:', cleanBranding);
      console.log('Stringified branding (first 200 chars):', brandingString.substring(0, 200));
      console.log('Is valid JSON?', brandingString.startsWith('{'));
      
      const response = await axios.put(
        `${API_URL}/business-info/site_branding`,
        { value: brandingString },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.status === 200) {
        toast.success('Site branding updated successfully!', {
          autoClose: 3000
        });
        // Reload the branding data to update the preview
        await loadBranding();
        
        // Dispatch event to notify Navbar and other components to reload branding
        window.dispatchEvent(new Event('brandingUpdated'));
      }
    } catch (error) {
      console.error('Save branding error:', error);
      if (error.response) {
        toast.error(`Failed to save branding: ${error.response.data.error || 'Server error'}`);
      } else if (error.request) {
        toast.error('Failed to save branding: No response from server');
      } else {
        toast.error('Failed to save branding: ' + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <Type className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Site Branding & Navigation</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Customize your site name and navigation menu labels. Changes will take effect after refreshing the page.
      </p>

      <div className="space-y-6">
        {/* Site Branding */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Site Logo & Name</h3>
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            {/* Logo Preview */}
            <div className="flex items-center space-x-4 p-4 bg-white rounded-lg border-2 border-gray-200">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden"
                style={{ backgroundColor: branding.logoBackgroundColor }}
              >
                {branding.logoType === 'image' && branding.logoImage ? (
                  <img src={branding.logoImage} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <span 
                    className="font-bold text-xl"
                    style={{ 
                      color: branding.logoTextColor,
                      fontFamily: branding.logoFontFamily 
                    }}
                  >
                    {branding.siteIcon}
                  </span>
                )}
              </div>
              <span 
                className="text-xl font-bold"
                style={{ 
                  color: branding.logoTextColor,
                  fontFamily: branding.logoFontFamily 
                }}
              >
                {branding.siteName}
              </span>
              <span className="text-sm text-gray-500 ml-auto">Preview</span>
            </div>

            {/* Logo Type Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logo Type
              </label>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => handleLogoTypeChange('emoji')}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                    branding.logoType === 'emoji'
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  Emoji/Text
                </button>
                <button
                  type="button"
                  onClick={() => handleLogoTypeChange('image')}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                    branding.logoType === 'image'
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  Custom Image
                </button>
              </div>
            </div>

            {/* Emoji Input */}
            {branding.logoType === 'emoji' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo Icon (emoji or single character)
                </label>
                <input
                  type="text"
                  value={branding.siteIcon}
                  onChange={(e) => handleSiteIcon(e.target.value.slice(0, 2))}
                  className="input max-w-xs text-center text-2xl"
                  placeholder="🐕"
                  maxLength={2}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tip: Use any emoji! Try: 🐕 🐩 ✂️ 🦴 🐾 ⭐ 💎 👑
                </p>
              </div>
            )}

            {/* Image Upload */}
            {branding.logoType === 'image' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Logo Image
                </label>
                {branding.logoImage ? (
                  <div className="flex items-center space-x-3">
                    <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-300">
                      <img src={branding.logoImage} alt="Logo" className="w-full h-full object-cover" />
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="btn bg-red-100 text-red-600 hover:bg-red-200 flex items-center space-x-2"
                    >
                      <X size={16} />
                      <span>Remove Image</span>
                    </button>
                  </div>
                ) : (
                  <div>
                    <label className="cursor-pointer inline-flex items-center space-x-2 px-4 py-2 bg-white border-2 border-gray-300 rounded-lg hover:border-primary-500 transition-colors">
                      <Upload size={18} />
                      <span>Choose Image</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-2">
                      Recommended: Square image, max 5MB (PNG, JPG, GIF, SVG)
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Site Name Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Title (shown in navbar)
              </label>
              <input
                type="text"
                value={branding.siteName}
                onChange={(e) => handleSiteName(e.target.value)}
                className="input max-w-md"
                placeholder="e.g., Dog Grooming"
              />
            </div>

            {/* Color and Font Customization */}
            <div className="border-t pt-4 space-y-4">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold text-gray-700">Logo Styling</h4>
                {branding.logoType === 'image' && (
                  <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                    Note: Text styling only applies to the site name when using custom images
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={branding.logoBackgroundColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, logoBackgroundColor: e.target.value }))}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.logoBackgroundColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, logoBackgroundColor: e.target.value }))}
                      className="input flex-1 font-mono text-sm"
                      placeholder="#2563eb"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Text Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={branding.logoTextColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, logoTextColor: e.target.value }))}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.logoTextColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, logoTextColor: e.target.value }))}
                      className="input flex-1 font-mono text-sm"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Font Family
                  </label>
                  <select
                    value={branding.logoFontFamily}
                    onChange={(e) => setBranding(prev => ({ ...prev, logoFontFamily: e.target.value }))}
                    className="input w-full"
                  >
                    <option value="system-ui">System Default</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                    <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                    <option value="Impact, sans-serif">Impact</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Navbar Background Color */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-700 mb-3">Navbar Background</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Navbar Background Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={branding.navbarBackgroundColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, navbarBackgroundColor: e.target.value }))}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.navbarBackgroundColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, navbarBackgroundColor: e.target.value }))}
                      className="input flex-1 font-mono text-sm"
                      placeholder="#ffffff"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Background color for the entire navigation bar</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Login Page Branding */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Login Page Branding</h3>
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            {/* Login Preview */}
            <div className="p-6 bg-white rounded-lg border-2 border-gray-200">
              <div className="flex flex-col items-center space-y-3">
                {/* Logo Above */}
                {branding.loginLogoAbove.enabled && (
                  <div 
                    className="rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0"
                    style={{ 
                      backgroundColor: branding.loginLogoAbove.backgroundColor,
                      width: `${branding.loginLogoAbove.size || 64}px`,
                      height: `${branding.loginLogoAbove.size || 64}px`
                    }}
                  >
                    {branding.loginLogoAbove.type === 'image' && branding.loginLogoAbove.image ? (
                      <img src={branding.loginLogoAbove.image} alt="Logo Above" className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-bold" style={{ color: '#ffffff', fontSize: `${Math.max(16, (branding.loginLogoAbove.size || 64) * 0.5)}px` }}>
                        {branding.loginLogoAbove.icon}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Title with Left and Right Logos */}
                <div className="flex items-center space-x-4">
                  {/* Logo Left */}
                  {branding.loginLogoLeft.enabled && (
                    <div 
                      className="rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0"
                      style={{ 
                        backgroundColor: branding.loginLogoLeft.backgroundColor,
                        width: `${branding.loginLogoLeft.size || 48}px`,
                        height: `${branding.loginLogoLeft.size || 48}px`
                      }}
                    >
                      {branding.loginLogoLeft.type === 'image' && branding.loginLogoLeft.image ? (
                        <img src={branding.loginLogoLeft.image} alt="Logo Left" className="w-full h-full object-cover" />
                      ) : (
                        <span className="font-bold" style={{ color: '#ffffff', fontSize: `${Math.max(14, (branding.loginLogoLeft.size || 48) * 0.5)}px` }}>
                          {branding.loginLogoLeft.icon}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Title */}
                  <span 
                    className="text-2xl font-bold"
                    style={{ 
                      color: branding.loginTextColor,
                      fontFamily: branding.loginFontFamily 
                    }}
                  >
                    {branding.loginTitle}
                  </span>
                  
                  {/* Logo Right */}
                  {branding.loginLogoRight.enabled && (
                    <div 
                      className="rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0"
                      style={{ 
                        backgroundColor: branding.loginLogoRight.backgroundColor,
                        width: `${branding.loginLogoRight.size || 48}px`,
                        height: `${branding.loginLogoRight.size || 48}px`
                      }}
                    >
                      {branding.loginLogoRight.type === 'image' && branding.loginLogoRight.image ? (
                        <img src={branding.loginLogoRight.image} alt="Logo Right" className="w-full h-full object-cover" />
                      ) : (
                        <span className="font-bold" style={{ color: '#ffffff', fontSize: `${Math.max(14, (branding.loginLogoRight.size || 48) * 0.5)}px` }}>
                          {branding.loginLogoRight.icon}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-4 text-center text-gray-600">{branding.loginSubtitle}</p>
            </div>

            {/* Logo Position Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <LoginLogoEditor
                position="loginLogoLeft"
                positionName="Left"
                logo={branding.loginLogoLeft}
                onToggle={handleLoginLogoToggle}
                onTypeChange={handleLoginLogoTypeChange}
                onIconChange={handleLoginLogoIcon}
                onImageUpload={handleLoginLogoImageUpload}
                onRemoveImage={handleRemoveLoginLogoImage}
                onBackgroundColorChange={handleLoginLogoBackgroundColor}
                onSizeChange={handleLoginLogoSize}
              />
              <LoginLogoEditor
                position="loginLogoAbove"
                positionName="Above"
                logo={branding.loginLogoAbove}
                onToggle={handleLoginLogoToggle}
                onTypeChange={handleLoginLogoTypeChange}
                onIconChange={handleLoginLogoIcon}
                onImageUpload={handleLoginLogoImageUpload}
                onRemoveImage={handleRemoveLoginLogoImage}
                onBackgroundColorChange={handleLoginLogoBackgroundColor}
                onSizeChange={handleLoginLogoSize}
              />
              <LoginLogoEditor
                position="loginLogoRight"
                positionName="Right"
                logo={branding.loginLogoRight}
                onToggle={handleLoginLogoToggle}
                onTypeChange={handleLoginLogoTypeChange}
                onIconChange={handleLoginLogoIcon}
                onImageUpload={handleLoginLogoImageUpload}
                onRemoveImage={handleRemoveLoginLogoImage}
                onBackgroundColorChange={handleLoginLogoBackgroundColor}
                onSizeChange={handleLoginLogoSize}
              />
            </div>

            {/* Login Title Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Login Page Title
              </label>
              <input
                type="text"
                value={branding.loginTitle}
                onChange={(e) => handleLoginTitle(e.target.value)}
                className="input max-w-md"
                placeholder="e.g., Dog Grooming System"
              />
            </div>

            {/* Login Subtitle Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Login Page Subtitle
              </label>
              <input
                type="text"
                value={branding.loginSubtitle}
                onChange={(e) => setBranding(prev => ({ ...prev, loginSubtitle: e.target.value }))}
                className="input max-w-md"
                placeholder="e.g., Sign in to your account"
              />
              <p className="text-xs text-gray-500 mt-1">
                Text displayed below the title on the login page
              </p>
            </div>

            {/* Login Logo & Title Styling */}
            <div className="border-t pt-4 space-y-4">
              <h4 className="font-semibold text-gray-700">Login Logo & Title Styling</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo Background Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={branding.loginLogoBackgroundColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, loginLogoBackgroundColor: e.target.value }))}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.loginLogoBackgroundColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, loginLogoBackgroundColor: e.target.value }))}
                      className="input flex-1 font-mono text-sm"
                      placeholder="#2563eb"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={branding.loginTextColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, loginTextColor: e.target.value }))}
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={branding.loginTextColor}
                      onChange={(e) => setBranding(prev => ({ ...prev, loginTextColor: e.target.value }))}
                      className="input flex-1 font-mono text-sm"
                      placeholder="#1f2937"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title Font Family
                  </label>
                  <select
                    value={branding.loginFontFamily}
                    onChange={(e) => setBranding(prev => ({ ...prev, loginFontFamily: e.target.value }))}
                    className="input w-full"
                  >
                    <option value="system-ui">System Default</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                    <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                    <option value="Impact, sans-serif">Impact</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Nav Menu Style */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-700 mb-3">Navigation Menu Style</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { value: 'auto', label: 'Auto', desc: 'Hamburger by default, horizontal when forced off' },
                  { value: 'horizontal', label: 'Horizontal', desc: 'Always show all items in a row across the top' },
                  { value: 'hamburger', label: 'Hamburger', desc: 'Always show a menu button with vertical dropdown' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setBranding(prev => ({ ...prev, navStyle: opt.value }))}
                    className={`text-left p-3 rounded-lg border-2 transition-colors ${
                      branding.navStyle === opt.value
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">{opt.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Labels */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Navigation Menu Labels & Styling</h3>
          <p className="text-sm text-gray-600 mb-4">
            Customize the text, color, font, and order for each navigation menu item. <strong>Drag and drop</strong> items to reorder them.
          </p>

          {/* Role Tabs */}
          <div className="flex space-x-2 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveNavTab('admin')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeNavTab === 'admin'
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Admin Navbar
            </button>
            <button
              onClick={() => setActiveNavTab('employee')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeNavTab === 'employee'
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Employee Navbar
            </button>
            <button
              onClick={() => setActiveNavTab('client')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeNavTab === 'client'
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Client Navbar
            </button>
          </div>

          {/* Info about current role */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              {activeNavTab === 'admin' && '👨‍💼 Customizing navbar for Admin users - full access to all features'}
              {activeNavTab === 'employee' && '👔 Customizing navbar for Employees/Groomers - limited to operational features'}
              {activeNavTab === 'client' && '👤 Customizing navbar for Clients - customer-facing features only'}
            </p>
          </div>

          {/* Reset Order to Defaults (nuclear option). Per-page reactivation
              lives below in the Deactivated Pages section. */}
          <div className="mb-4 flex justify-end">
            <button
              type="button"
              onClick={handleResetNavOrderToDefault}
              className="btn flex items-center space-x-2 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              title="Reset this role's navbar to factory defaults (order and pages). Custom labels, colors, and fonts are kept. Confirmation required."
            >
              <RotateCcw size={16} />
              <span>Reset Order to Defaults</span>
            </button>
          </div>

          {/* Deactivated Pages (per-role). Items in DEFAULT_NAV_ORDER for this
              role that have been removed from navOrder. Each shows a
              Reactivate button that appends the page back to the end of the
              order. Custom labels / colors / fonts persist via navLabels and
              navLabelStyles even while deactivated. */}
          {(() => {
            const role = activeNavTab;
            const defaults = DEFAULT_NAV_ORDER[role] || [];
            const current = branding.navOrder[role] || [];
            const deactivated = defaults.filter(key => !current.includes(key));
            if (deactivated.length === 0) return null;
            return (
              <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center space-x-2">
                  <X size={18} className="text-gray-500" />
                  <span>Deactivated Pages ({deactivated.length})</span>
                </h4>
                <p className="text-xs text-gray-600 mb-3">
                  These default pages are hidden from the {role} navbar. Click Reactivate to add a page back -- it will appear at the end of the order, where you can drag it into position. Click Save Branding afterward to persist the change.
                </p>
                <div className="flex flex-wrap gap-2">
                  {deactivated.map(key => {
                    const label = branding.navLabels[key] || NAV_KEY_FALLBACK_LABELS[key] || key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleReactivatePage(key)}
                        className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-white text-primary-700 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors"
                        title={`Reactivate "${label}" in the ${role} navbar`}
                      >
                        <RotateCcw size={14} />
                        <span>Reactivate "{label}"</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div className="space-y-4">
            {getFilteredNavOrder().map((key, index) => {
              const value = branding.navLabels[key];
              return (
              <div 
                key={key} 
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDrop={handleDrop}
                className={`bg-gray-50 p-4 rounded-lg border-2 cursor-move transition-all ${
                  draggedIndex === index 
                    ? 'border-primary-600 shadow-lg scale-105' 
                    : 'border-gray-200 hover:border-primary-400 hover:shadow-md'
                }`}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <GripVertical className="text-gray-400" size={20} />
                  <label className="block text-sm font-semibold text-gray-800 capitalize flex-1">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">Position: {index + 1}</span>
                  <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); handleDeactivatePage(key); }}
                    draggable={false}
                    className="flex items-center space-x-1 px-2 py-1 text-xs text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded"
                    title="Deactivate this page (hide from navbar). You can reactivate it from the Deactivated Pages section below."
                  >
                    <X size={14} />
                    <span>Deactivate</span>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Label Text */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Label Text
                    </label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleNavLabelChange(key, e.target.value)}
                      className="input"
                      placeholder={`e.g., ${value}`}
                    />
                  </div>

                  {/* Text Color */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Text Color
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={branding.navLabelStyles[key]?.color || '#374151'}
                        onChange={(e) => {
                          setBranding(prev => ({
                            ...prev,
                            navLabelStyles: {
                              ...prev.navLabelStyles,
                              [key]: {
                                ...prev.navLabelStyles[key],
                                color: e.target.value
                              }
                            }
                          }));
                        }}
                        className="w-10 h-9 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={branding.navLabelStyles[key]?.color || '#374151'}
                        onChange={(e) => {
                          setBranding(prev => ({
                            ...prev,
                            navLabelStyles: {
                              ...prev.navLabelStyles,
                              [key]: {
                                ...prev.navLabelStyles[key],
                                color: e.target.value
                              }
                            }
                          }));
                        }}
                        className="input flex-1 font-mono text-xs"
                        placeholder="#ffffff"
                      />
                    </div>
                  </div>

                  {/* Font Family */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Font Family
                    </label>
                    <select
                      value={branding.navLabelStyles[key]?.fontFamily || 'system-ui'}
                      onChange={(e) => {
                        setBranding(prev => ({
                          ...prev,
                          navLabelStyles: {
                            ...prev.navLabelStyles,
                            [key]: {
                              ...prev.navLabelStyles[key],
                              fontFamily: e.target.value
                            }
                          }
                        }));
                      }}
                      className="input w-full text-sm"
                    >
                      <option value="system-ui">System Default</option>
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="'Times New Roman', serif">Times New Roman</option>
                      <option value="'Courier New', monospace">Courier New</option>
                      <option value="Georgia, serif">Georgia</option>
                      <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                      <option value="Verdana, sans-serif">Verdana</option>
                      <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                      <option value="Impact, sans-serif">Impact</option>
                    </select>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-3 pt-3 border-t border-gray-300">
                  <span className="text-xs text-gray-500 mr-2">Preview:</span>
                  <span 
                    className="text-sm font-medium"
                    style={{
                      color: branding.navLabelStyles[key]?.color || '#374151',
                      fontFamily: branding.navLabelStyles[key]?.fontFamily || 'system-ui',
                      backgroundColor: '#ffffff',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      display: 'inline-block',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    {value}
                  </span>
                </div>
              </div>
            );
            })}
          </div>
        </div>

        {/* Info Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> After saving changes, you'll need to refresh your browser to see the updated branding, colors, fonts, and navigation labels.
          </p>
        </div>

        {/* Save Button - Prominent */}
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Ready to save your changes?</h4>
              <p className="text-sm text-gray-600">Click the button to save all logo, color, font, and navigation customizations.</p>
            </div>
            <button
              onClick={handleSaveBranding}
              disabled={saving}
              className="btn btn-primary flex items-center space-x-2 text-lg px-6 py-3"
            >
              <Save size={20} />
              <span>{saving ? 'Saving...' : 'Save Branding'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteBrandingEditor;
